/**
 * JobFormV2 — the Submit Job flow for everyone (contractor "Submit Job" tab
 * and the admin "Log Job" tab).
 *
 * Progressive-disclosure form (Claude Design handoff): 7 numbered sections that
 * open one at a time, collapse to a one-line summary with Edit when complete,
 * and unlock the next. Sticky progress bar on top, sticky Submit at the bottom.
 *
 * Functional logic preserved from the earlier build:
 *  - adaptive segmented stock input (letter → 7 boxes, Suzuki digit → 6 boxes)
 *  - brand from stock prefix + brand logo
 *  - car-history lookup on completion → First Clean auto-select (new cars only)
 *  - combined "Paint Protect + Clayblock" routes Clayblock to the brand dept
 *    with a "PAINT PROTECT" comment; standalone Clayblock inherits the main job
 *  - colour swatches, comment presets, office-cleaner exclusion, multi-detailer
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { FormHeader } from './formKit';
import { HOUR_OPTS } from './jobPricing';

/* ---- design tokens ---------------------------------------------------- */
const TEAL = '#4A7A91', TEAL_TINT = '#e8f2f7', NAVY = '#1e3a4a';
const YELLOW = '#FFD000', GREEN = '#059669', CHECK = '#16a34a';
const PAGE = '#f4f6f8', LINE = '#e5e7eb', TRACK = '#eceef0';
const G400 = '#9ca3af', G500 = '#6b7280', G700 = '#374151', G300 = '#d1d5db';
const MONO = 'ui-monospace, Menlo, monospace';

const PUB2 = process.env.PUBLIC_URL + '/brand-logos';
const BRAND_LOGO_MAP = { 'KIA CARS': 'kia.png', 'GWM CARS': 'gwm.png', 'GAC CARS': 'gac.png', 'SUZUKI CARS': 'suzuki.png', 'USED CARS': 'used.png' };

/* ---- services (display label → canonical value used by the backend) --- */
const SERVICE_GROUPS = [
  { group: 'STANDARD CLEAN', items: [
    { label: 'FIRST CLEAN', value: 'FIRST CLEAN', firstClean: true },
    { label: 'DELIVERY', value: 'DELIVERY' },
    { label: 'DEMO', value: 'DEMO' },
    { label: 'RECLEAN', value: 'RECLEAN' },
  ] },
  { group: 'FULL DETAIL', items: [
    { label: 'MINI', value: 'MINI DETAIL' },
    { label: 'SMALL', value: 'SMALL FULL DETAIL' },
    { label: 'MEDIUM', value: 'MEDIUM FULL DETAIL' },
    { label: 'LARGE', value: 'LARGE FULL DETAIL' },
    { label: 'EXTRA LARGE', value: 'EXTRA LARGE FULL DETAIL' },
  ] },
];

// No-car services (daily contract / bulk counts) — logged through the separate
// "Daily · Yard · Showroom" branch, not the per-car flow.
const NC_SERVICES = ['SERVICE WASH DAILY', 'YARD CLEAN', 'SHOWROOM CARS'];
const NC_DEPTS = [
  { label: 'KIA', value: 'KIA CARS' }, { label: 'GWM', value: 'GWM CARS' },
  { label: 'SUZUKI', value: 'SUZUKI CARS' }, { label: 'GAC', value: 'GAC CARS' },
  { label: 'USED', value: 'USED CARS' },
];
// Billed as a fraction of an 8-hour day (units = hours / 8). Nothing above 8h
// has ever been logged, so 10 and 12 only made a mis-tap easier; 2h has, and
// wasn't on the list.
const SWD_HOURS = [2, 4, 5, 6, 7, 8];
const SERVICE_LABEL = {};
SERVICE_GROUPS.forEach(g => g.items.forEach(it => { SERVICE_LABEL[it.value] = it.label; }));

const COMMENT_PRESETS = ['DEMO', 'DELIVERY', 'YARD', 'LOAN CAR', 'CUSTOMER CAR', 'N/A'];

const COLOURS = [
  { name: 'WHITE', sw: '#fff', bd: '1px solid #d1d5db' }, { name: 'BLACK', sw: '#0a0a0a' },
  { name: 'SILVER', sw: '#c0c6cc' }, { name: 'GREY', sw: '#6b7280' },
  { name: 'BLUE', sw: '#2563eb' }, { name: 'RED', sw: '#dc2626' },
  { name: 'GREEN', sw: '#16a34a' }, { name: 'YELLOW', sw: '#F5C518' },
  { name: 'BEIGE', sw: '#d6c6a8' }, { name: 'OTHER', sw: '#fff', bd: '1.5px dashed #9ca3af', plus: true },
];

// A NEW-car dealership stock. Kia/GWM = letter + 6 digits; GAC = A + one
// alphanumeric + 5 digits (covers both A123456 and the newer AF12602 / AK12356
// VIN-based numbers); Suzuki = 6 digits. First Clean only applies to these —
// used cars (5 digits) and number plates never auto-pick First Clean.
const isNewCarStock = (s) => {
  const t = String(s || '').trim().toUpperCase();
  return /^[KG]\d{6}$/.test(t) || /^A[A-Z0-9]\d{5}$/.test(t) || /^\d{6}$/.test(t);
};

// Brand department from the stock prefix (K→Kia, G→GWM, A→GAC, 6 digits→Suzuki,
// 5 digits→Used). GAC now allows a second letter (AF12602), so match any
// A-prefix. A Detail-Queue prefill wins.
function brandFromStock(stock, prefillDept) {
  if (prefillDept) return prefillDept;
  const s = String(stock || '').trim().toUpperCase();
  if (/^K\d/.test(s)) return 'KIA CARS';
  if (/^G\d/.test(s)) return 'GWM CARS';
  if (/^A/.test(s)) return 'GAC CARS';
  if (/^\d{6}/.test(s)) return 'SUZUKI CARS';
  if (/^\d{5}$/.test(s)) return 'USED CARS';
  return null;
}
// Expected length of a segmented stock: letter-first → 7 (K/G/A + 6 digits);
// digit-first → the chosen digit length (Suzuki 6 or Used 5).
const stockLen = (s, digitLen) => (/^\d/.test(String(s || '')) ? (digitLen || 6) : 7);
const fmtDMY = (iso) => iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '';

/* ---- shared chip ------------------------------------------------------ */
const chip = (active, big) => ({
  fontSize: big ? 13 : 11, fontWeight: 700, letterSpacing: 0.3, padding: big ? '9px 14px' : '6px 10px',
  borderRadius: 8, cursor: 'pointer', textTransform: 'uppercase', lineHeight: 1.1, userSelect: 'none',
  border: `1.5px solid ${active ? TEAL : LINE}`, background: active ? TEAL_TINT : '#fff', color: active ? NAVY : G700,
});

/* ---- collapsible section wrapper (module scope → stable identity) ------ */
function SectionShell({ n, title, open, done, locked, summary, onEdit, children }) {
  if (locked) {
    return (
      <div style={{ background: '#fff', padding: '15px 16px', opacity: 0.5, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 15 }}>🔒</span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400 }}>{n} · {title}</span>
      </div>
    );
  }
  if (done && !open) {
    return (
      <div onClick={onEdit} style={{ background: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
        <span style={{ width: 18, height: 18, borderRadius: '50%', background: CHECK, color: '#fff', fontSize: 12, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>✓</span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400, flex: 'none' }}>{n} · {title}</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e', marginLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{summary}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: TEAL, flex: 'none' }}>Edit</span>
      </div>
    );
  }
  return (
    <div style={{ background: '#fff', padding: '16px' }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400, marginBottom: 12 }}>{n} · {title}</div>
      {children}
    </div>
  );
}

/* ---- adaptive segmented stock input ----------------------------------- */
// Letter in box 0 (K/G/A) → 7 boxes (letter + 6 digits); a digit in box 0 →
// 6 boxes (Suzuki, all digits). Auto-advances; backspace steps back; onComplete
// fires when the whole code is filled (runs the history lookup).
function SegmentedStock({ value, onChange, onComplete, digitLen }) {
  const refs = useRef([]);
  const firstIsDigit = /^\d/.test(value || '');
  const dl = digitLen || 6;
  const n = (value && firstIsDigit) ? dl : 7;
  const chars = Array.from({ length: n }, (_, i) => (value || '')[i] || '');
  const setChar = (i, c) => {
    const arr = [...chars]; arr[i] = c;
    const nn = (arr[0] && /^\d/.test(arr[0])) ? dl : 7;
    const joined = arr.slice(0, nn).join('').toUpperCase();
    onChange(joined);
    if (c && i < nn - 1) refs.current[i + 1] && refs.current[i + 1].focus();
    if (joined.length === nn) { setTimeout(() => onComplete && onComplete(joined), 0); if (refs.current[i]) refs.current[i].blur(); }
  };
  const onKey = (i, e) => {
    if (e.key === 'Backspace' && !chars[i] && i > 0) refs.current[i - 1] && refs.current[i - 1].focus();
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {chars.map((c, i) => {
        const filled = !!c, isFirst = i === 0;
        return (
          <input key={i} ref={el => { refs.current[i] = el; }} value={c} maxLength={1}
            inputMode={(i === 0 || (i === 1 && /^A/i.test(value || ''))) ? 'text' : 'numeric'}
            onChange={e => {
              let v = e.target.value.slice(-1);
              // Box 0 and — for GAC (A-prefix) — box 1 accept a letter; the rest digits.
              const alnum = i === 0 || (i === 1 && /^A/i.test(value || ''));
              v = alnum ? v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() : v.replace(/[^0-9]/g, '');
              setChar(i, v);
            }}
            onKeyDown={e => onKey(i, e)}
            style={{
              width: 38, height: 48, textAlign: 'center', fontSize: 20, fontWeight: 800, fontFamily: MONO,
              color: isFirst && filled ? NAVY : '#1a1a2e', borderRadius: 10, outline: 'none', boxSizing: 'border-box',
              border: `1.5px solid ${(isFirst && filled) ? TEAL : LINE}`,
              background: (isFirst && filled) ? TEAL_TINT : '#fff', textTransform: 'uppercase',
            }} />
        );
      })}
    </div>
  );
}

export default function JobFormV2({ user, onJobSubmitted, onViewMyJobs, prefill }) {
  const [carModels, setCarModels] = useState({});
  const [contractors, setContractors] = useState([]);

  const [jobMode, setJobMode] = useState('car');       // 'car' | 'nocar'
  const [stockMode, setStockMode] = useState('stock'); // 'stock' | 'plate'
  const [stock, setStock] = useState('');
  const [stockDigitLen, setStockDigitLen] = useState(6); // digit-first: 6 Suzuki / 5 Used
  const [privateCar, setPrivateCar] = useState(false);   // books against the private customer
  const [plateDone, setPlateDone] = useState(false);
  // A private car is not sold off the dealership's menu. Ticking "Private
  // car" used to leave the detailer choosing between FIRST CLEAN / DELIVERY /
  // DEMO / RECLEAN, so a retail Simple Clean was logged as a DEMO and a $600
  // ceramic coating as a $65 paint protection.
  const [privServices, setPrivServices] = useState([]);

  // No-car branch (Service Wash Daily / Yard Clean / Showroom Cars).
  const [ncService, setNcService] = useState('');      // one of NC_SERVICES
  const [swdHours, setSwdHours] = useState(8);
  const [carCount, setCarCount] = useState(1);
  const [ncDept, setNcDept] = useState('');            // brand dept for yard/showroom
  const [ncStep2Done, setNcStep2Done] = useState(false);
  const [model, setModel] = useState('');
  const [colour, setColour] = useState('');           // swatch name or 'OTHER'
  const [customColour, setCustomColour] = useState('');
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const [service, setService] = useState('');          // canonical value
  const [extras, setExtras] = useState(new Set());     // BUFFING, STRIP TINT, SEAT CLEANING, CLAYBLOCK, PP_CLAY
  // Hours is the one extra that isn't a single unit — it's billed per hour, so
  // it needs a quantity the others don't. Admin edit has always had it; the
  // form didn't, so extra hours could only be added after the fact.
  const [extraHours, setExtraHours] = useState(0);
  const [extrasDone, setExtrasDone] = useState(false);
  const [commentChip, setCommentChip] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentsDone, setCommentsDone] = useState(false);
  const [damageChoice, setDamageChoice] = useState(''); // '' | 'none' | 'report'
  const [damageDesc, setDamageDesc] = useState('');
  const [employeeIds, setEmployeeIds] = useState(user?.type === 'admin' ? [] : [user?.id]);
  const [detailerDone, setDetailerDone] = useState(false);

  const [editing, setEditing] = useState(null);        // section index being edited (overrides firstIncomplete)
  const [melvilleId, setMelvilleId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.getCarModels().then(setCarModels).catch(() => {});
    api.getActiveContractors().then(r => setContractors(Array.isArray(r) ? r : (r.contractors || []))).catch(() => {});
  }, []);

  // Prefill from the Detail Queue.
  useEffect(() => {
    if (!prefill) return;
    if (prefill.stock_number) setStock(prefill.stock_number);
    if (prefill.model) setModel(prefill.model);
    if (prefill.colour) setColour((prefill.colour || '').toUpperCase());
    if (prefill.melville_vehicle_id) setMelvilleId(prefill.melville_vehicle_id);
    // A booked private car arrives with the services the customer already
    // agreed to. The detailer confirms them rather than reconstructing the
    // sale from memory — first one is the job, the rest ride as extras.
    if (prefill.private) {
      setPrivateCar(true);
      setStockMode('plate');
      const svcs = (prefill.services || []).map(s => String(s).toUpperCase());
      if (svcs.length) {
        setService(svcs[0]);
        setExtras(new Set(svcs.slice(1)));
        setExtrasDone(true);
      }
    }
  }, [prefill]);

  const brand = brandFromStock(stock, prefill?.dept);
  const brandModels = (brand && carModels[brand] ? carModels[brand].map(m => m.model || m) : []).slice().sort();
  const allowFirstClean = isNewCarStock(stock) && (!history || history.is_new);
  const detailers = contractors.filter(c => c.type !== 'office_cleaner');
  const finalColour = colour === 'OTHER' ? customColour.trim().toUpperCase() : colour;

  // Look up car history. Fired only when the stock code is complete (or plate
  // Continue) — not per keystroke. First Clean is auto-picked for new cars.
  const lookup = useCallback(async (sn) => {
    const s = String(sn || '').trim();
    if (!s) { setHistory(null); return; }
    setHistLoading(true);
    try {
      const h = await api.getCarHistory(s);
      setHistory(h);
      if (h.is_new && isNewCarStock(s)) setService('FIRST CLEAN');
      else setService(sv => sv === 'FIRST CLEAN' ? '' : sv);
      setModel(m => m || h.last_model || '');
      setColour(c => c || (h.last_colour ? String(h.last_colour).toUpperCase() : ''));
    } catch { setHistory(null); }
    finally { setHistLoading(false); }
  }, []);

  // Stock changed: clear stale history, and when the segmented code is complete
  // run the lookup + advance past section 1.
  useEffect(() => {
    setHistory(null);
    if (stockMode === 'stock' && stock && stock.length === stockLen(stock, stockDigitLen)) {
      lookup(stock);
      setEditing(e => (e === 0 ? null : e));
    }
    // eslint-disable-next-line
  }, [stock, stockMode, stockDigitLen]);

  // Loaded once, the first time a private car is ticked.
  useEffect(() => {
    if (!privateCar || privServices.length) return;
    api.getPrivateServices()
      .then(rows => setPrivServices(rows.map(r => String(r.service_name).toUpperCase())))
      .catch(() => { /* fall back to the standard menu */ });
  }, [privateCar, privServices.length]);

  const toggleExtra = (k) => setExtras(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    if (k === 'PP_CLAY' && next.has('PP_CLAY')) next.delete('CLAYBLOCK');
    if (k === 'CLAYBLOCK' && next.has('CLAYBLOCK')) next.delete('PP_CLAY');
    return next;
  });
  const toggleEmp = (id) => setEmployeeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  /* ---- progressive-disclosure state machine --------------------------- */
  const isSWD = ncService === 'SERVICE WASH DAILY';
  const carDone = [
    stockMode === 'stock' ? (!!stock && stock.length === stockLen(stock, stockDigitLen)) : plateDone,
    !!(model.trim() && finalColour),
    !!service,
    extrasDone,
    commentsDone,
    damageChoice !== '',
    detailerDone && employeeIds.length > 0,
  ];
  const ncDone = [
    !!ncService,
    ncStep2Done,
    detailerDone && employeeIds.length > 0,
  ];
  const done = jobMode === 'car' ? carDone : ncDone;
  const total = done.length;
  let current = done.findIndex(x => !x); if (current === -1) current = total;
  const openIdx = editing != null ? editing : current;
  const doneCount = done.filter(Boolean).length;
  const allDone = doneCount === total;
  const isOpen = (i) => openIdx === i;
  const isDone = (i) => done[i] && openIdx !== i;
  const isLocked = (i) => !done[i] && openIdx !== i && i > current;
  const editSetter = (i) => () => setEditing(i);
  const switchMode = (m) => { if (m !== jobMode) { setJobMode(m); setEditing(null); } };

  // Auto-collapse sections when they become complete via a single choice.
  const pickService = (v) => { setService(v); setEditing(e => (e === 2 ? null : e)); };
  const pickColour = (name) => {
    setColour(name);
    if (name !== 'OTHER' && model.trim()) setEditing(e => (e === 1 ? null : e));
  };
  const pickModel = (m) => {
    setModel(m);
    if (colour && colour !== 'OTHER') setEditing(e => (e === 1 ? null : e));
  };

  const canSubmit = allDone && !loading;

  async function submit() {
    if (!canSubmit) { setError('Complete all sections first.'); return; }
    setError(''); setLoading(true);
    try {
      // No-car branch: Service Wash Daily (hours) / Yard Clean · Showroom (car
      // count + department). No stock, model, colour, extras or damage.
      if (jobMode === 'nocar') {
        const payload = isSWD
          ? { stock_number: 'SWD', model: 'Daily Wash', job_description: 'SERVICE WASH DAILY', department: 'SERVICE', units: swdHours / 8 }
          : { stock_number: 'N/A', model: ncService, job_description: ncService, department: ncDept, units: carCount };
        await api.submitJob({
          date: new Date().toISOString().slice(0, 10),
          ...payload, comments: [], extra_jobs: [], damage_defects: false, employee_ids: employeeIds,
        });
        setSuccess(true);
        if (onJobSubmitted) onJobSubmitted();
        setLoading(false);
        return;
      }

      const isFirst = service === 'FIRST CLEAN';
      const extraJobs = [];
      if (privateCar && privServices.length) {
        // The private list is free-form, so every ticked chip is its own
        // service by name — no fixed set to translate.
        for (const s of extras) extraJobs.push({ service: s, units: 1 });
        if (extraHours > 0) extraJobs.push({ service: 'HOURS', units: extraHours });
      } else {
        if (extras.has('BUFFING'))       extraJobs.push({ service: 'BUFFING', units: 1 });
        if (extras.has('STRIP TINT'))    extraJobs.push({ service: 'STRIP TINT', units: 1 });
        if (extras.has('SEAT CLEANING')) extraJobs.push({ service: 'SEAT CLEANING', units: 1 });
        if (extraHours > 0)              extraJobs.push({ service: 'HOURS', units: extraHours });
        if (extras.has('PP_CLAY')) {
          extraJobs.push({ service: 'PAINT PROTECTION', units: 1 });
          extraJobs.push({ service: 'CLAYBLOCK', units: 1, department: brand || null, comment: 'PAINT PROTECT' });
        } else if (extras.has('CLAYBLOCK')) {
          extraJobs.push({ service: 'CLAYBLOCK', units: 1 });
        }
      }
      const comments = [];
      if (commentChip && commentChip !== 'N/A') comments.push(commentChip);
      if (commentText.trim()) comments.push(commentText.trim());

      await api.submitJob({
        date: new Date().toISOString().slice(0, 10),
        stock_number: stock.trim(),
        model: model.trim(),
        colour: finalColour,
        job_description: service,
        comments,
        extra_jobs: extraJobs,
        units: 1,
        damage_defects: damageChoice === 'report',
        employee_ids: employeeIds,
        // PRIVATE routes the job to the private customer, so a personal car
        // never lands in the dealership's reports. One spelling only — this
        // used to send PRIVATE CAR while the rest of the app said PRIVATE,
        // and a single car came out split across two departments.
        department: privateCar ? 'PRIVATE' : (isFirst ? 'SERVICE' : (brand || undefined)),
        // Ties the work back to the quote the customer accepted, so the
        // invoice bills what was agreed instead of what someone re-typed.
        private_quote_id: privateCar ? (prefill?.private_quote_id || undefined) : undefined,
      });

      if (melvilleId) {
        try { await api.melvilleMarkDetailed({ vehicle_id: melvilleId, stock_number: stock.trim(), detailer: user?.name, paint: extras.has('PP_CLAY') }); } catch { /* soft */ }
      }
      setSuccess(true);
      if (onJobSubmitted) onJobSubmitted();
    } catch (err) {
      setError(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setJobMode('car');
    setStockMode('stock'); setStock(''); setPlateDone(false); setModel(''); setColour(''); setCustomColour('');
    setHistory(null); setService(''); setExtras(new Set()); setExtraHours(0); setExtrasDone(false);
    setCommentChip(''); setCommentText(''); setCommentsDone(false); setDamageChoice(''); setDamageDesc('');
    setNcService(''); setSwdHours(8); setCarCount(1); setNcDept(''); setNcStep2Done(false); setPrivateCar(false);
    setEmployeeIds(user?.type === 'admin' ? [] : [user?.id]); setDetailerDone(false);
    setEditing(null); setMelvilleId(null); setSuccess(false); setError('');
  }

  /* ---- success screen ------------------------------------------------- */
  if (success) {
    return (
      <div style={{ background: PAGE, minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
        <span style={{ width: 64, height: 64, borderRadius: '50%', background: CHECK, color: '#fff', fontSize: 34, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
        <h2 style={{ color: '#1a1a2e', margin: '16px 0 4px', fontSize: 22, fontWeight: 800 }}>Job submitted!</h2>
        <p style={{ color: G500, margin: 0 }}>Waiting for admin approval ⏳</p>
        <p style={{ color: G400, fontSize: 13, margin: '6px 0 0' }}>
          {jobMode === 'nocar'
            ? (isSWD ? `Service Wash Daily · ${swdHours}h` : `${ncService} · ${carCount} car${carCount === 1 ? '' : 's'} · ${(NC_DEPTS.find(d => d.value === ncDept) || {}).label || ''}`)
            : `${model} · ${finalColour} · ${stock}`}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
          <button onClick={reset} style={{ background: TEAL, color: '#fff', border: 'none', borderRadius: 12, padding: '12px 22px', fontWeight: 800, cursor: 'pointer' }}>Submit another job</button>
          {onViewMyJobs && <button onClick={onViewMyJobs} style={{ background: '#fff', color: NAVY, border: `1px solid ${LINE}`, borderRadius: 12, padding: '12px 22px', fontWeight: 800, cursor: 'pointer' }}>View jobs</button>}
        </div>
      </div>
    );
  }

  /* ---- summaries ------------------------------------------------------ */
  const sums = [
    stockMode === 'stock' ? stock : stock.toUpperCase(),
    `${model.trim().toUpperCase()} · ${finalColour}`,
    SERVICE_LABEL[service] || service,
    (() => {
      const list = [...extras].map(e => (e === 'PP_CLAY' ? 'PAINT PROTECT + CLAYBLOCK' : e));
      if (extraHours > 0) list.push(`${extraHours}H`);
      return list.length ? list.join(' · ') : 'NONE';
    })(),
    commentChip || (commentText.trim() ? 'NOTE' : '—'),
    damageChoice === 'report' ? 'DAMAGE ⚠' : 'NO DAMAGE',
    employeeIds.map(id => (detailers.find(c => c.id === id) || {}).name).filter(Boolean).join(', '),
  ];

  return (
    <div style={{ background: PAGE, minHeight: '100vh' }}>
      <FormHeader title="Submit Job" doneCount={doneCount} total={total} />

      <div style={{ maxWidth: 620, margin: '0 auto', padding: '10px 0 24px' }}>
        {/* car vs no-car (Daily · Yard · Showroom) mode toggle */}
        <div style={{ padding: '2px 16px 12px' }}>
          <div style={{ display: 'flex', gap: 6, background: '#eef1f4', padding: 4, borderRadius: 12 }}>
            <span onClick={() => switchMode('car')} style={{ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 800, letterSpacing: 0.3, padding: '9px 0', borderRadius: 9, cursor: 'pointer', color: jobMode === 'car' ? NAVY : G500, background: jobMode === 'car' ? '#fff' : 'transparent', boxShadow: jobMode === 'car' ? '0 1px 3px rgba(0,0,0,.08)' : 'none' }}>🚗 CAR</span>
            <span onClick={() => switchMode('nocar')} style={{ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 800, letterSpacing: 0.3, padding: '9px 0', borderRadius: 9, cursor: 'pointer', color: jobMode === 'nocar' ? NAVY : G500, background: jobMode === 'nocar' ? '#fff' : 'transparent', boxShadow: jobMode === 'nocar' ? '0 1px 3px rgba(0,0,0,.08)' : 'none' }}>🧹 DAILY · YARD · SHOWROOM</span>
          </div>
        </div>

        {jobMode === 'nocar' ? (
          <NoCarFlow
            isOpen={isOpen} isDone={isDone} isLocked={isLocked} editSetter={editSetter} setEditing={setEditing}
            ncService={ncService} setNcService={setNcService} setNcStep2Done={setNcStep2Done}
            isSWD={isSWD} swdHours={swdHours} setSwdHours={setSwdHours}
            carCount={carCount} setCarCount={setCarCount} ncDept={ncDept} setNcDept={setNcDept} ncStep2Done={ncStep2Done}
            detailers={detailers} employeeIds={employeeIds} toggleEmp={toggleEmp} detailerDone={detailerDone} setDetailerDone={setDetailerDone}
          />
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* 1 — IDENTIFICATION */}
          <SectionShell n="1" title="IDENTIFICATION" open={isOpen(0)} done={isDone(0)} locked={isLocked(0)} summary={sums[0]} onEdit={editSetter(0)}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <span onClick={() => { if (stockMode !== 'stock') { setStockMode('stock'); setStock(''); setPlateDone(false); setHistory(null); } }} style={chip(stockMode === 'stock', true)}>Stock Number</span>
              <span onClick={() => { if (stockMode !== 'plate') { setStockMode('plate'); setStock(''); setPlateDone(false); setHistory(null); } }} style={chip(stockMode === 'plate', true)}>Plate</span>
              {/* A private car isn't dealership work — it books against the
                  walk-in/private customer instead of Melville. */}
              <span onClick={() => {
                  setPrivateCar(v => !v);
                  // The two menus don't overlap — carrying a pick across would
                  // log a retail service as dealership work, or the reverse.
                  setService(''); setExtras(new Set()); setExtrasDone(false);
                  if (!privateCar) { setStockMode('plate'); setStock(''); setPlateDone(false); setHistory(null); }
                }}
                style={chip(privateCar, true)}>Private car</span>
            </div>
            {stockMode === 'stock' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <SegmentedStock value={stock} onChange={setStock} onComplete={(v) => lookup(v)} digitLen={stockDigitLen} />
                  {brand && BRAND_LOGO_MAP[brand] && (
                    <img src={`${PUB2}/${BRAND_LOGO_MAP[brand]}`} alt={brand} style={{ width: 44, height: 32, objectFit: 'contain', flex: 'none' }} />
                  )}
                </div>
                {/^\d/.test(stock) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500 }}>DIGITS</span>
                    <span onClick={() => { setStockDigitLen(6); setStock(s => s.slice(0, 6)); }} style={chip(stockDigitLen === 6)}>Suzuki · 6</span>
                    <span onClick={() => { setStockDigitLen(5); setStock(s => s.slice(0, 5)); }} style={chip(stockDigitLen === 5)}>Used · 5</span>
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: G400, marginTop: 10 }}>Tap and type — first box is a letter (K / G / A sets the brand). A digit means Suzuki (6) or a used car (5).</div>
              </>
            ) : (
              <>
                <input value={stock} onChange={e => setStock(e.target.value.toUpperCase())} placeholder="e.g. 1ABC234"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${stock ? TEAL : LINE}`, borderRadius: 10, padding: '11px 13px', outline: 'none', fontSize: 18, fontWeight: 800, letterSpacing: 1.5, color: NAVY, fontFamily: MONO, textTransform: 'uppercase' }} />
                <button onClick={() => { if (stock.trim().length >= 3) { setPlateDone(true); setEditing(e => (e === 0 ? null : e)); lookup(stock); } }} disabled={stock.trim().length < 3}
                  style={{ marginTop: 12, height: 38, padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', cursor: stock.trim().length >= 3 ? 'pointer' : 'not-allowed', background: stock.trim().length >= 3 ? TEAL : TRACK, ...(stock.trim().length < 3 ? { color: G400 } : {}) }}>Continue</button>
              </>
            )}
            {histLoading && <div style={{ fontSize: 12, color: G400, marginTop: 10 }}>Checking history…</div>}
          </SectionShell>

          {/* Car context — history / new-car note. Stays visible after section 1
              collapses (it lives outside the collapsible block). */}
          {history && !histLoading && (
            history.is_new ? (
              <div style={{ background: '#fff', padding: '12px 16px', fontSize: 12.5, color: '#15803d', fontWeight: 700 }}>🆕 New car — First Clean auto-selected.</div>
            ) : (
              <div style={{ background: '#fff', padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500, marginBottom: 8 }}>🕑 CAR HISTORY ({history.count} service{history.count === 1 ? '' : 's'})</div>
                {history.history.slice(0, 5).map((h, i) => (
                  <div key={i} style={{ fontSize: 12.5, color: '#334155', display: 'flex', justifyContent: 'space-between', marginTop: i ? 5 : 0 }}>
                    <span>{fmtDMY(h.date)} · {h.service}</span><span style={{ color: G400 }}>{h.department || '—'}</span>
                  </div>
                ))}
              </div>
            )
          )}

          {/* 2 — CAR */}
          <SectionShell n="2" title="CAR" open={isOpen(1)} done={isDone(1)} locked={isLocked(1)} summary={sums[1]} onEdit={editSetter(1)}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500, marginBottom: 8 }}>MODEL</div>
            {brandModels.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {brandModels.map(m => <span key={m} onClick={() => pickModel(m)} style={chip(model === m)}>{m}</span>)}
              </div>
            )}
            <input value={model} onChange={e => setModel(e.target.value)} placeholder="Model"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 14, outline: 'none' }} />

            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500, margin: '16px 0 10px' }}>COLOUR <span style={{ color: '#dc2626' }}>*</span></div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {COLOURS.map(c => {
                const sel = colour === c.name;
                return (
                  <div key={c.name} onClick={() => pickColour(c.name)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', width: 40 }}>
                    <span style={{ width: 34, height: 34, borderRadius: 8, background: c.sw, border: sel ? `2.5px solid ${TEAL}` : (c.bd || '1.5px solid transparent'), boxShadow: sel ? `0 0 0 3px ${TEAL_TINT}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: G400, fontSize: 18, fontWeight: 700, boxSizing: 'border-box' }}>{c.plus ? '+' : ''}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: sel ? NAVY : G500 }}>{c.name}</span>
                  </div>
                );
              })}
            </div>
            {colour === 'OTHER' && (
              <input value={customColour} onChange={e => setCustomColour(e.target.value)} placeholder="Colour name" autoFocus
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 14, marginTop: 12, outline: 'none', textTransform: 'uppercase' }} />
            )}
          </SectionShell>

          {/* 3 — SERVICE */}
          <SectionShell n="3" title="SERVICE" open={isOpen(2)} done={isDone(2)} locked={isLocked(2)} summary={sums[2]} onEdit={editSetter(2)}>
            {privateCar && privServices.length ? (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.8, color: G400, marginBottom: 8 }}>PRIVATE / RETAIL</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {privServices.map(s => (
                    <span key={s} onClick={() => pickService(s)} style={chip(service === s)}>{s}</span>
                  ))}
                </div>
              </div>
            ) : SERVICE_GROUPS.map(g => {
              const items = g.items.filter(it => !it.firstClean || allowFirstClean);
              if (!items.length) return null;
              return (
                <div key={g.group} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.8, color: G400, marginBottom: 8 }}>{g.group}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {items.map(it => <span key={it.value} onClick={() => pickService(it.value)} style={chip(service === it.value)}>{it.label}</span>)}
                  </div>
                </div>
              );
            })}
          </SectionShell>

          {/* 4 — EXTRAS */}
          <SectionShell n="4" title="EXTRAS" open={isOpen(3)} done={isDone(3)} locked={isLocked(3)} summary={sums[3]} onEdit={editSetter(3)}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {privateCar && privServices.length ? (
                // Everything else on the private list. One car often carries
                // three or four of them — the Simple Clean + Buffing +
                // Clayblock + Ceramic Coating job is a normal retail ticket.
                privServices.filter(s => s !== service).map(s => (
                  <span key={s} onClick={() => toggleExtra(s)} style={chip(extras.has(s))}>{s}</span>
                ))
              ) : (
                <>
                  <span onClick={() => toggleExtra('BUFFING')} style={chip(extras.has('BUFFING'))}>Buffing</span>
                  <span onClick={() => toggleExtra('STRIP TINT')} style={chip(extras.has('STRIP TINT'))}>Strip Tint</span>
                  <span onClick={() => toggleExtra('SEAT CLEANING')} style={chip(extras.has('SEAT CLEANING'))}>Seat Cleaning</span>
                  <span onClick={() => toggleExtra('CLAYBLOCK')} style={chip(extras.has('CLAYBLOCK'))}>Clayblock</span>
                  <span onClick={() => toggleExtra('PP_CLAY')} style={chip(extras.has('PP_CLAY'))}>Paint Protect + Clayblock</span>
                </>
              )}
              <span onClick={() => setExtraHours(h => (h ? 0 : 1))} style={chip(extraHours > 0)}>Hours</span>
              <span onClick={() => { setExtras(new Set()); setExtraHours(0); setExtrasDone(true); setEditing(e => (e === 3 ? null : e)); }} style={chip(extrasDone && extras.size === 0 && !extraHours)}>None</span>
            </div>

            {extraHours > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: G400, marginBottom: 8 }}>HOW MANY HOURS</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {HOUR_OPTS.map(h => (
                    <span key={h} onClick={() => setExtraHours(h)} style={chip(extraHours === h)}>{h}h</span>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => { setExtrasDone(true); setEditing(e => (e === 3 ? null : e)); }}
              style={{ marginTop: 14, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: TEAL, cursor: 'pointer' }}>Continue</button>
          </SectionShell>

          {/* 5 — COMMENTS */}
          <SectionShell n="5" title="COMMENTS" open={isOpen(4)} done={isDone(4)} locked={isLocked(4)} summary={sums[4]} onEdit={editSetter(4)}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {COMMENT_PRESETS.map(p => (
                <span key={p} onClick={() => setCommentChip(cur => cur === p ? '' : p)} style={chip(commentChip === p)}>{p}</span>
              ))}
            </div>
            <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Extra notes…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 14, outline: 'none' }} />
            <button onClick={() => { setCommentsDone(true); setEditing(e => (e === 4 ? null : e)); }}
              style={{ marginTop: 14, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: TEAL, cursor: 'pointer' }}>Continue</button>
          </SectionShell>

          {/* 6 — DAMAGE */}
          <SectionShell n="6" title="DAMAGE" open={isOpen(5)} done={isDone(5)} locked={isLocked(5)} summary={sums[5]} onEdit={editSetter(5)}>
            <div style={{ display: 'flex', gap: 8 }}>
              <span onClick={() => { setDamageChoice('none'); setEditing(e => (e === 5 ? null : e)); }} style={chip(damageChoice === 'none', true)}>None</span>
              <span onClick={() => setDamageChoice('report')} style={chip(damageChoice === 'report', true)}>Report Damage</span>
            </div>
            {damageChoice === 'report' && (
              <>
                <textarea value={damageDesc} onChange={e => setDamageDesc(e.target.value)} placeholder="Describe the damage…" rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid #fca5a5', borderRadius: 8, fontSize: 14, marginTop: 10, resize: 'vertical', outline: 'none' }} />
                <div style={{ fontSize: 10.5, color: G400, marginTop: 6 }}>Photo upload is coming in a later pass.</div>
              </>
            )}
          </SectionShell>

          {/* 7 — DETAILING BY */}
          <SectionShell n="7" title="DETAILING BY" open={isOpen(6)} done={isDone(6)} locked={isLocked(6)} summary={sums[6]} onEdit={editSetter(6)}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {detailers.map(c => <span key={c.id} onClick={() => toggleEmp(c.id)} style={chip(employeeIds.includes(c.id))}>{c.name}</span>)}
            </div>
            <button onClick={() => { if (employeeIds.length) { setDetailerDone(true); setEditing(e => (e === 6 ? null : e)); } }} disabled={!employeeIds.length}
              style={{ marginTop: 14, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: employeeIds.length ? TEAL : TRACK, cursor: employeeIds.length ? 'pointer' : 'not-allowed', ...(employeeIds.length ? {} : { color: G400 }) }}>Continue</button>
          </SectionShell>

          {/* SUMMARY (all done) */}
          {allDone && (
            <div style={{ background: '#fff', padding: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: G400, marginBottom: 12 }}>SUMMARY</div>
              <div style={{ background: PAGE, borderRadius: 12, padding: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {brand && BRAND_LOGO_MAP[brand] && <img src={`${PUB2}/${BRAND_LOGO_MAP[brand]}`} alt={brand} style={{ width: 46, height: 42, objectFit: 'contain', flex: 'none' }} />}
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>{model.trim().toUpperCase()} {finalColour}</div>
                  <div style={{ fontSize: 12, color: G500, fontFamily: MONO }}>{stock.toUpperCase()} · {sums[6]}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '4px 9px', borderRadius: 999, background: TEAL_TINT, color: TEAL }}>{sums[2]}</span>
                  {commentChip && <span style={{ fontSize: 10, fontWeight: 800, padding: '4px 9px', borderRadius: 999, background: TEAL_TINT, color: TEAL }}>{commentChip}</span>}
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '4px 9px', borderRadius: 999, background: damageChoice === 'report' ? '#fee2e2' : '#dcfce7', color: damageChoice === 'report' ? '#b91c1c' : '#15803d' }}>{sums[5]}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 12px', borderRadius: 8, fontSize: 13, margin: '10px 16px 0' }}>{error}</div>}

        {/* sticky submit */}
        <div style={{ position: 'sticky', bottom: 8, padding: '12px 16px 0', marginTop: 10 }}>
          <button onClick={submit} disabled={!canSubmit}
            style={{ width: '100%', height: 50, borderRadius: 14, border: 'none', fontSize: 15, fontWeight: 800, color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed', background: canSubmit ? GREEN : G300, boxShadow: canSubmit ? '0 6px 18px rgba(5,150,105,.32)' : 'none' }}>
            {loading ? 'Submitting…' : (allDone ? 'Submit Job' : `Submit Job · ${total - doneCount} to go`)}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- no-car flow (Service Wash Daily / Yard Clean / Showroom Cars) ------ */
function NoCarFlow({
  isOpen, isDone, isLocked, editSetter, setEditing,
  ncService, setNcService, setNcStep2Done,
  isSWD, swdHours, setSwdHours, carCount, setCarCount, ncDept, setNcDept, ncStep2Done,
  detailers, employeeIds, toggleEmp, detailerDone, setDetailerDone,
}) {
  const pickService = (v) => { setNcService(v); setNcStep2Done(false); setEditing(0); };
  const step2Title = !ncService ? 'DETAILS' : (isSWD ? 'HOURS' : 'CARS');
  const step2Summary = isSWD
    ? `${swdHours}h`
    : `${carCount} car${carCount === 1 ? '' : 's'} · ${(NC_DEPTS.find(d => d.value === ncDept) || {}).label || '—'}`;
  const detailerSummary = employeeIds.map(id => (detailers.find(c => c.id === id) || {}).name).filter(Boolean).join(', ');
  const step2Ready = isSWD ? swdHours > 0 : (!!ncDept && carCount >= 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* 1 — SERVICE */}
      <SectionShell n="1" title="SERVICE" open={isOpen(0)} done={isDone(0)} locked={isLocked(0)} summary={ncService} onEdit={editSetter(0)}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {NC_SERVICES.map(s => <span key={s} onClick={() => pickService(s)} style={chip(ncService === s)}>{s}</span>)}
        </div>
      </SectionShell>

      {/* 2 — HOURS (SWD) or CARS + DEPARTMENT (Yard / Showroom) */}
      <SectionShell n="2" title={step2Title} open={isOpen(1)} done={isDone(1)} locked={isLocked(1)} summary={step2Summary} onEdit={editSetter(1)}>
        {isSWD ? (
          <>
            <div style={{ fontSize: 11.5, color: G400, marginBottom: 12 }}>Service Wash Daily — hours worked today.</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SWD_HOURS.map(h => (
                <span key={h} onClick={() => { setSwdHours(h); setNcStep2Done(true); setEditing(e => (e === 1 ? null : e)); }}
                  style={{ width: 52, textAlign: 'center', fontSize: 15, fontWeight: 800, padding: '11px 0', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${swdHours === h && ncStep2Done ? TEAL : LINE}`, background: swdHours === h && ncStep2Done ? TEAL_TINT : '#fff', color: swdHours === h && ncStep2Done ? NAVY : G700 }}>{h}h</span>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500, marginBottom: 8 }}>DEPARTMENT</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {NC_DEPTS.map(d => <span key={d.value} onClick={() => setNcDept(d.value)} style={chip(ncDept === d.value)}>{d.label}</span>)}
            </div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: G500, marginBottom: 10 }}>QUANTITY OF CARS</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span onClick={() => setCarCount(c => Math.max(1, c - 1))} style={{ width: 44, height: 44, borderRadius: 12, border: `1.5px solid ${LINE}`, fontSize: 22, fontWeight: 800, color: G500, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }}>−</span>
              <span style={{ fontSize: 30, fontWeight: 800, color: '#1a1a2e', fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'center' }}>{carCount}</span>
              <span onClick={() => setCarCount(c => c + 1)} style={{ width: 44, height: 44, borderRadius: 12, border: `1.5px solid ${TEAL}`, background: TEAL_TINT, fontSize: 22, fontWeight: 800, color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }}>+</span>
            </div>
          </>
        )}
        {!isSWD && (
          <button onClick={() => { if (step2Ready) { setNcStep2Done(true); setEditing(e => (e === 1 ? null : e)); } }} disabled={!step2Ready}
            style={{ marginTop: 16, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: step2Ready ? TEAL : TRACK, cursor: step2Ready ? 'pointer' : 'not-allowed', ...(step2Ready ? {} : { color: G400 }) }}>Continue</button>
        )}
      </SectionShell>

      {/* 3 — DETAILING BY */}
      <SectionShell n="3" title="DETAILING BY" open={isOpen(2)} done={isDone(2)} locked={isLocked(2)} summary={detailerSummary} onEdit={editSetter(2)}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {detailers.map(c => <span key={c.id} onClick={() => toggleEmp(c.id)} style={chip(employeeIds.includes(c.id))}>{c.name}</span>)}
        </div>
        <button onClick={() => { if (employeeIds.length) { setDetailerDone(true); setEditing(e => (e === 2 ? null : e)); } }} disabled={!employeeIds.length}
          style={{ marginTop: 14, height: 40, padding: '0 20px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: employeeIds.length ? TEAL : TRACK, cursor: employeeIds.length ? 'pointer' : 'not-allowed', ...(employeeIds.length ? {} : { color: G400 }) }}>Continue</button>
      </SectionShell>
    </div>
  );
}
